import { apiBuilder, prefixApi } from "@zodios/core";
import { z } from "zod";

const advertisementBodySchema = z.object({
  id: z.number().optional(),
  products: z.array(z.number()).optional(),
  market_place: z.string().optional(),
  name: z.string().optional(),
  channel: z.string().optional(),
  state: z.number().optional(),
  category: z.number().optional(),
  ad_type: z.union([z.number(), z.string()]).optional(),
  targeting_type: z.string().optional(),
  daily_budget: z.number().optional(),
  target_acos: z.union([z.number(), z.string()]).optional(),
  max_acos: z.union([z.number(), z.string()]).optional(),
  ad_goal: z.union([z.number(), z.string()]).optional(),
  parent: z.number().nullable().optional(),
  budget_allocation: z.number().optional(),
  is_composite: z.boolean().optional(),
  attributes: z.record(z.unknown()).optional(),
  notification_settings: z.record(z.unknown()).optional(),
  themed_kw_list: z.array(z.string()).optional(),
  negative_kw_list: z.array(z.string()).optional(),
  themed_asin_list: z.array(z.string()).optional(),
  negative_asin_list: z.array(z.string()).optional(),
  discover_keywords: z.boolean().optional(),
  brand_entity: z.string().nullable().optional(),
  store_name: z.string().nullable().optional(),
  page_name: z.string().nullable().optional(),
  creative: z.unknown().optional(),
  landing_page: z.record(z.unknown()).optional(),
  user_category_id_list: z.array(z.unknown()).optional(),
  custom_audience_id_list: z.array(z.unknown()).optional(),
  non_trellis_enrolled_ad: z.boolean().optional(),
  non_trellis_enrolled_ad_accept_bids: z.boolean().optional(),
  non_trellis_enrolled_ad_accept_bids_start_date: z.string().nullable().optional(),
  creative_display_metadata: z.record(z.unknown()).optional(),
  tags: z.array(z.string()).optional(),
  ad_control_fields: z.record(z.unknown()).optional(),
});

const createAdvertisementSchema = advertisementBodySchema;

const adPropertyUpdateSchema = advertisementBodySchema.extend({
  custom_api_request: z.literal("update_advertisement"),
  id: z.number(),
  child_data: z
    .array(
      z
        .object({
          id: z.number(),
          name: z.string().optional(),
          budget_allocation: z.number().optional(),
          target_acos: z.union([z.number(), z.string()]).optional(),
          max_acos: z.union([z.number(), z.string()]).optional(),
        })
        .passthrough(),
    )
    .optional(),
});

const atomicUpdateV2Schema = z.union([
  z.object({
    field: z.literal("tags"),
    action: z.union([z.literal("set"), z.literal("remove"), z.literal("add")]),
    value: z.union([z.string().array(), z.number().array()]),
  }),
  z.object({
    field: z.literal("cancel_scheduled_actions"),
    action: z.literal("set"),
    value: z.literal("cancelled"),
  }),
  z.object({
    field: z.union([
      z.literal("daily_budget"),
      z.literal("target_acos"),
      z.literal("max_acos"),
      z.literal("target_roas"),
      z.literal("min_roas"),
      z.literal("min_bid"),
      z.literal("max_bid"),
    ]),
    action: z.union([z.literal("set"), z.literal("percent"), z.literal("fixed")]),
    value: z.number(),
  }),
  z.object({
    field: z.union([
      z.literal("bidding_rules"),
      z.literal("budget_rules"),
      z.literal("category"),
      z.literal("week_parting_method"),
    ]),
    action: z.literal("set"),
    value: z.string().or(z.number()).or(z.null()), // ID, normally a number, but backend will coerce
  }),
  z.object({
    field: z.union([
      z.literal("bid_adjustments_by_placement"),
      z.literal("shopper_cohort_bidding"),
      z.literal("bid_adjustments_by_shopper_segment"),
    ]),
    action: z.literal("set"),
    value: z.record(z.string().regex(/^\d+$/), z.array(z.object({}).passthrough())),
  }),
  z.object({
    field: z.union([z.literal("bid_algo"), z.literal("sales_volume_control_slider")]),
    action: z.literal("set"),
    value: z.record(z.string().regex(/^\d+$/), z.union([z.string(), z.number(), z.null()])),
  }),
  z.object({
    field: z.literal("bidding_strategy"),
    action: z.literal("set"),
    value: z.union([
      z.literal("manual"),
      z.literal("legacyForSales"),
      z.literal("MAXIMIZE_IMMEDIATE_SALES"),
      z.literal("MAXIMIZE_NEW_TO_BRAND_CUSTOMERS"),
      z.literal("RULE_BASED"),
      z.literal("LEGACY_FOR_SALES"),
      z.literal("AUTO_FOR_SALES"),
    ]),
  }),
  z.object({
    field: z.union([
      z.literal("non_trellis_enrolled_ad"),
      z.literal("non_trellis_enrolled_ad_accept_bids"),
      z.literal("algo_managed_placement_multipliers"),
      z.literal("auto_negation"),
      z.literal("push_to_amazon"),
    ]),
    action: z.literal("set"),
    value: z.boolean(),
  }),
  z.object({
    field: z.literal("algo_managed_placement_multipliers"),
    action: z.literal("set"),
    value: z.boolean(),
  }),
  z.object({
    field: z.literal("week_parting_multipliers"),
    action: z.literal("set"),
    value: z
      .array(
        z.object({
          weekday: z.number().int().min(0).max(6),
          multiplier: z.number().max(1.5),
        }),
      )
      .or(z.null()),
  }),
  z.object({
    field: z.union([z.literal("target_acos_negation_threshold"), z.literal("cumm_threshold")]),
    action: z.literal("set"),
    value: z.number().or(z.null()),
  }),
  z.object({
    field: z.literal("products"),
    action: z.literal("add"),
    value: z.array(z.number()),
  }),
]);

const adPropertyUpdateSchemaV2 = z.object({
  advertisements: z.array(z.number()),
  updates: z.array(atomicUpdateV2Schema),
});

const bulkAdUpdateErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  details: z.record(z.unknown()).optional(),
});

const bulkAdUpdateResultSchema = z.object({
  ad_id: z.number(),
  ad_name: z.string(),
  field: z.string(),
  action: z.string(),
  value: z.unknown(),
  error: bulkAdUpdateErrorSchema.nullable(),
});

const bulkAdUpdateResponseSchema = z.array(bulkAdUpdateResultSchema);

const adDaypartingUpdateSchema = z.object({
  advertisement_ids: z.array(z.number()),
  day_parting_method: z.any(),
  day_parting_multipliers: z.any(),
});

const adScheduledActionsSchema = z.object({
  channel: z.string(),
  start_date: z.string(),
  end_date: z.string(),
  status: z.string(),
  gvadvertisements_set: z.array(z.number()),
  fields: z.array(
    z.object({
      field_name: z.string(),
      change_type: z.string(),
      change_value: z.number(),
    }),
  ),
});

const adTargetUpdateSchema = z.object({
  data: z.object({
    advertisement: z.number(),
    channel: z.string(),
    target_type: z.string(),
    action: z.literal("create_targets"),
    ad_group: z.optional(z.string()),
    targets: z.optional(
      z.union([
        z.array(
          z.object({
            text: z.string(),
            matchTypes: z.array(z.string()),
          }),
        ),
        z.object({
          products: z.array(z.string()),
          targetTypes: z.array(z.string()),
        }),
      ]),
    ),
  }),
});

// All-optional: a strict shape would turn an explainable refusal back into a bare parse failure.
// Mirrors the payload, not today's readers — zod strips what it does not declare.
const addTargetsToAdResponseSchema = z.object({
  created_count: z.number().optional(),
  skipped_targets: z
    .array(
      z.object({
        keyword: z.string().optional(),
        products: z.array(z.string()).optional(),
        match_types: z.array(z.string()).optional(),
        reason: z.string().optional(),
        code: z.string().optional(),
      }),
    )
    .optional(),
});

const adGroupSchema = z.object({
  ad_group_id: z.string(),
  ad_group_name: z.string(),
});

const bulkTargetActionSchema = z.object({
  table: z.union([z.literal("keyword"), z.literal("target")]),
  filters: z
    .array(
      z.object({
        id: z.string(),
        value: z.unknown(),
      }),
    )
    .optional(),
  ordering: z.string().optional(),
  limit: z.number().optional(),
  marketplace: z.string(),
  start_date: z.string(),
  end_date: z.string(),
  start_date_previous: z.string(),
  end_date_previous: z.string(),
  grouping: z.string().optional(),
  search: z.array(z.string()).optional(),
  data: z.object({
    target_type: z.union([z.literal("keyword"), z.literal("target")]),
    channel: z.string(),
    action: z.union([
      z.literal("change_state"),
      z.literal("set_bid"),
      z.literal("set_bid_range"),
      z.literal("negate_target"),
      z.literal("update_tags"),
      z.literal("add_tags"),
      z.literal("remove_tags"),
      z.literal("create_scheduled_actions"),
      z.literal("delete_scheduled_actions"),
    ]),
    values: z.array(z.unknown()),
    marketplace: z.string(),
  }),
});

const targetActionBaseSchema = z.object({ target_type: z.string(), channel: z.string() });
const targetIdsActionSchema = targetActionBaseSchema.extend({ target_ids: z.array(z.number()) });
const targetTagValuesSchema = z.array(z.union([z.string(), z.number()]));

const targetActionSchema = z.object({
  data: z.discriminatedUnion("action", [
    targetActionBaseSchema.extend({
      action: z.literal("set_bid"),
      bids: z.record(z.string(), z.number().nullable()),
    }),
    targetIdsActionSchema.extend({
      action: z.literal("set_bid_range"),
      // [ceil_bid, floor_bid]; null leaves that side unbounded
      values: z.tuple([z.number().nullable(), z.number().nullable()]),
    }),
    targetIdsActionSchema.extend({ action: z.literal("change_state"), values: z.tuple([z.string()]) }),
    targetIdsActionSchema.extend({ action: z.literal("negate_target"), values: z.tuple([]) }),
    targetIdsActionSchema.extend({ action: z.literal("add_tags"), values: targetTagValuesSchema }),
    targetIdsActionSchema.extend({ action: z.literal("remove_tags"), values: targetTagValuesSchema }),
  ]),
});

export type CreateAdvertisement = z.infer<typeof createAdvertisementSchema>;
export type AdPropertyUpdate = z.infer<typeof adPropertyUpdateSchema>;
export type BulkAdPropertyUpdate = z.infer<typeof adPropertyUpdateSchemaV2>;
export type BulkAdUpdateResult = z.infer<typeof bulkAdUpdateResultSchema>;
export type BulkAdUpdateError = z.infer<typeof bulkAdUpdateErrorSchema>;
export type BulkAdDaypartingUpdate = z.infer<typeof adDaypartingUpdateSchema>;
export type BulkAdScheduledActionsUpdate = z.infer<typeof adScheduledActionsSchema>;
export type AddTargetsToAdUpdate = z.infer<typeof adTargetUpdateSchema>;

export const adsApiDefinitions = prefixApi(
  "/api",
  apiBuilder({
    alias: "setAdState",
    method: "post",
    path: "/set_campaign_state/",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ id: z.number().or(z.string()), state: z.literal(0).or(z.literal(1)) }),
      },
    ],
    status: 200,
    response: z.object({
      new_campaign_state: z.number(),
    }),
    errors: [
      {
        status: 400,
        schema: z.object({}),
      },
    ],
  })
    .addEndpoint({
      alias: "createAdvertisement",
      method: "post",
      path: "/gvads/advproduct/",
      parameters: [
        {
          name: "body",
          type: "Body",
          schema: createAdvertisementSchema,
        },
      ],
      status: 201,
      response: z.object({ advertisement_id: z.number() }),
    })
    .addEndpoint({
      alias: "updateAdProperties",
      method: "put",
      // "custom_api_request" isn't actually a query parameter that backend accepts, backend expects it in the body. We're using it to differentiate between different ways to call this endpoint
      path: "/gvads/advproduct/?custom_api_request=update_advertisement",
      parameters: [
        {
          name: "body",
          type: "Body",
          schema: adPropertyUpdateSchema,
        },
      ],
      status: 200,
      response: z.unknown(),
    })
    .addEndpoint({
      alias: "updateAdPropertiesV2",
      method: "post",
      path: "/ads/bulk_update/",
      parameters: [
        {
          name: "body",
          type: "Body",
          schema: adPropertyUpdateSchemaV2,
        },
      ],
      status: 200,
      response: bulkAdUpdateResponseSchema,
    })
    .addEndpoint({
      alias: "updateDayparting",
      method: "post",
      path: "/bulk_update_dayparting/",
      parameters: [
        {
          name: "body",
          type: "Body",
          schema: adDaypartingUpdateSchema,
        },
      ],
      status: 200,
      response: z.unknown(),
    })
    .addEndpoint({
      alias: "updateAdScheduledActions",
      method: "post",
      path: "/scheduledactions/",
      parameters: [
        {
          name: "body",
          type: "Body",
          schema: adScheduledActionsSchema,
        },
      ],
      status: 200,
      response: z.unknown(),
    })
    .addEndpoint({
      alias: "addTargetsToAd",
      method: "post",
      // The query only keeps this path distinct from targetAction's for Zodios; the server reads the body's action
      path: "/target_actions/?action=create_targets",
      parameters: [
        {
          name: "body",
          type: "Body",
          schema: adTargetUpdateSchema,
        },
      ],
      status: 200,
      response: addTargetsToAdResponseSchema,
    })
    .addEndpoint({
      alias: "bulkTargetAction",
      method: "post",
      path: "/bulk_target_actions/",
      parameters: [
        {
          name: "body",
          type: "Body",
          schema: bulkTargetActionSchema,
        },
      ],
      status: 200,
      response: z.object({
        status: z.string(),
        details: z.string(),
      }),
    })
    .addEndpoint({
      alias: "targetAction",
      method: "post",
      path: "/target_actions/",
      parameters: [
        {
          name: "body",
          type: "Body",
          schema: targetActionSchema,
        },
      ],
      status: 200,
      response: z.unknown(),
    })
    .addEndpoint({
      alias: "getAdGroups",
      method: "get",
      path: "/ad_groups",
      status: 200,
      response: z.array(adGroupSchema),
      parameters: [
        {
          name: "advertisement",
          type: "Query",
          schema: z.number(),
        },
      ],
    })
    .addEndpoint({
      alias: "getBiddingPrograms",
      method: "get",
      path: "/programs?engine=bidding",
      status: 200,
      parameters: [
        {
          name: "all_user_orgs",
          type: "Query",
          schema: z.boolean().optional(),
        },
      ],
      response: z.object({
        results: z.array(
          z.object({
            id: z.number(),
            name: z.string(),
            org: z.object({
              name: z.string(),
              id: z.number(),
            }),
          }),
        ),
      }),
    })
    .build(),
);
